import { WIKI_RESOLUTION_NOTE_MAX } from "@esi/shared";
import { Check } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// The decision note on Resolve (wiki-collaboration-ux D16): one optional line, kept on the
// thread and shown with its resolution wherever the thread can be read.

/** "Resolved by Carol", then " · <note>" when the resolver left one. */
export function useResolvedLine() {
  const { t } = useTranslation();
  return (thread: {
    resolvedByName: string | null;
    resolutionNote?: string | null | undefined;
  }) => {
    const name = thread.resolvedByName ?? t("wiki.threads.someone");
    return thread.resolutionNote
      ? t("wiki.threads.resolvedWithNote", { name, note: thread.resolutionNote })
      : t("wiki.threads.resolvedBy", { name });
  };
}

/**
 * The resolve step: the optional note on one line, then Resolve (or Reply & resolve when a reply
 * goes with it) and Cancel. Enter resolves, Escape cancels; focus starts in the note.
 */
export function ResolveStep(props: {
  busy: boolean;
  /** "Reply & resolve": the reply is posted first. */
  withReply?: boolean;
  onResolve: (note: string | undefined) => Promise<unknown> | undefined;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [note, setNote] = useState("");
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    input.current?.focus();
  }, []);
  const length = Array.from(note.normalize("NFC").trim()).length;
  const tooLong = length > WIKI_RESOLUTION_NOTE_MAX;
  return (
    <form
      data-resolve-step=""
      className="flex flex-col gap-2 rounded-lg border border-line bg-surface p-3"
      onSubmit={async (e) => {
        e.preventDefault();
        if (tooLong) {
          return;
        }
        try {
          await props.onResolve(note.trim() || undefined);
        } catch {
          // The caller shows the refusal.
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          props.onCancel();
        }
      }}
    >
      <Label htmlFor={`${id}-note`} className="text-xs font-medium text-fg-secondary">
        {t("wiki.threads.note.label")}
      </Label>
      <Input
        ref={input}
        id={`${id}-note`}
        value={note}
        // One line: a pasted line break reads as a space.
        // One line, no control characters (a pasted tab or break reads as a space).
        onChange={(e) => setNote(e.target.value.replace(/\p{Cc}+/gu, " "))}
        placeholder={t("wiki.threads.note.placeholder")}
        disabled={props.busy}
        aria-invalid={tooLong ? true : undefined}
        aria-describedby={`${id}-hint`}
        className="h-8 text-sm"
      />
      <p id={`${id}-hint`} className={`text-xs ${tooLong ? "text-danger-fg" : "text-fg-muted"}`}>
        {tooLong
          ? t("wiki.threads.note.tooLong", { max: WIKI_RESOLUTION_NOTE_MAX })
          : t("wiki.threads.note.hint")}
      </p>
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={props.busy}
          onClick={props.onCancel}
        >
          {t("app.cancel")}
        </Button>
        <Button type="submit" size="sm" disabled={props.busy || tooLong}>
          <Check aria-hidden="true" />
          {props.withReply ? t("wiki.threads.note.replyConfirm") : t("wiki.threads.note.confirm")}
        </Button>
      </div>
    </form>
  );
}
